package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initPercentilesDontAveragePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/PercentilesDontAverage.md", FrontMatterElement.Builder { addScalar("title", "Percentiles don't average"); addScalar("searchTitle", "Monitoring Ktor services in Kotlin/Native without Prometheus"); addScalar("date", "2026-05-24"); addScalar("description", "metrik costs the monitored service 106 nanoseconds per request and a UDP packet a minute — and the reason it merges percentiles instead of averaging them is that averaging them is simply wrong."); addScalar("library", "metrik"); }))
}

@Page("/blog/percentiles-dont-average")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun PercentilesDontAveragePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There is a gap between \"")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kubectl logs")
        }
        org.jetbrains.compose.web.dom.Text(" is enough\" and \"let's run Prometheus, Grafana and a time-series database\". ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/metrik") {
            org.jetbrains.compose.web.dom.Text("metrik")
        }
        org.jetbrains.compose.web.dom.Text(" lives in it: one native binary, one SQLite file, no scrape configuration and no service discovery.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It answers four questions — how many requests, how many errors, how slow, is memory running out — and writes to Telegram when the answers get worse.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-agent-has-to-be-free") }) {
        org.jetbrains.compose.web.dom.Text("The agent has to be free")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An agent that costs the monitored service real latency will be turned off during the incident it was installed for. So the budget was: measurable, and small enough that nobody argues.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It comes to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("106 nanoseconds per request")
        }
        org.jetbrains.compose.web.dom.Text(" and one UDP packet per minute. The aggregation happens in-process; nothing is scraped, so there is no endpoint to discover and no scheduler to misconfigure.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No JMX either. The JVM build reads ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Runtime")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ProcessHandle")
        }
        org.jetbrains.compose.web.dom.Text("; the native build reads ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("percentiles-do-not-average") }) {
        org.jetbrains.compose.web.dom.Text("Percentiles do not average")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the one I would put on a poster.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three instances report p99 latencies of 100 ms, 100 ms and 400 ms. What is the p99 of the service?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It is not 200 ms. The average of percentiles is not a percentile of anything — it is a number with no meaning that happens to be in the right range, which is worse than no number, because it looks fine.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("metrik ships exponential histograms and merges them. Merging histograms and then reading the quantile gives the actual p99 across instances, and it stays correct when one instance is having a bad time and the others are not — which is precisely when you are looking.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Route templates are the other half of keeping this honest. Series are labelled ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/users/{id}")
        }
        org.jetbrains.compose.web.dom.Text(", not ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/users/8134")
        }
        org.jetbrains.compose.web.dom.Text(", so cardinality stays bounded instead of growing one series per user.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("worse-needs-worse-after-what") }) {
        org.jetbrains.compose.web.dom.Text("\"Worse\" needs \"worse after what\"")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A latency chart that goes up tells you nothing you can act on. The useful question is always ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("what changed")
        }
        org.jetbrains.compose.web.dom.Text(", and the answer is almost always a deploy.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So every chart carries deploy markers. It is a small feature and it is the one that turns looking at a graph into finding a cause.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Alerts fire on error rate, latency, memory and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("silence")
        }
        org.jetbrains.compose.web.dom.Text(" — with hysteresis and a cooldown, because an alert that flaps is an alert that gets muted. Silence matters as much as the rest: a service that stopped reporting looks identical to a healthy one on every chart.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-ways-to-read-it") }) {
        org.jetbrains.compose.web.dom.Text("Three ways to read it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The dashboard is Compose Multiplatform, light and dark. There is also a terminal client — the same numbers, one native binary, no browser.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And there is an MCP endpoint, so an agent can query the metrics directly instead of being handed a link to a page it cannot see. That turns out to be the one I use most: \"why did p99 move at 14:20\" is a question worth asking in the same place you are already working.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("deployment") }) {
        org.jetbrains.compose.web.dom.Text("Deployment")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Zero runtime dependencies — Kotlin/Native, storage in SQLite through sqlx4k, and authentication delegated to whatever reverse proxy is already in front (oauth2-proxy, Traefik, NGINX). One binary, one file.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/metrik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/metrik")
        }
        org.jetbrains.compose.web.dom.Text(" · live at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://metrik.kotlin.website") {
            org.jetbrains.compose.web.dom.Text("metrik.kotlin.website")
        }
    }
}
